import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { DeleteConfirmModal } from "../components/DeleteConfirmModal";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Building2, UserPlus, CalendarCheck, Download, Copy, ShieldCheck, QrCode } from "lucide-react";

// Ensures URL is always absolute — uses window.location.origin if backend returns a relative path
const resolveUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${window.location.origin}${url.startsWith("/") ? "" : "/"}${url}`;
};

export default function Branches() {
  const { setBranches, fetchUser } = useAuth();
  const [branchList, setBranchList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", phone: "" });
  const [qrData, setQrData] = useState(null);
  const [qrBranch, setQrBranch] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, branch: null });
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => { fetchBranches(); }, []);

  const fetchBranches = async () => {
    try {
      const res = await api.get("/branches");
      setBranchList(res.data.branches);
      setBranches(res.data.branches);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/branches/${editing.id}`, form);
        toast.success("Branch updated");
      } else {
        await api.post("/branches", form);
        toast.success("Branch created");
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: "", address: "", phone: "" });
      fetchBranches();
      fetchUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.branch) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/branches/${deleteModal.branch.id}`);
      toast.success(`"${deleteModal.branch.name}" has been removed`);
      setDeleteModal({ open: false, branch: null });
      fetchBranches();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleQR = async (branch, qrType = "attendance") => {
    try {
      const res = await api.get(`/branches/${branch.id}/qr?qr_type=${qrType}`);
      setQrData(res.data);
      setQrBranch(branch);
    } catch {
      toast.error("Failed to generate QR");
    }
  };

  const downloadQR = () => {
    if (!qrData) return;
    const link = document.createElement("a");
    link.download = `${qrBranch?.name || "branch"}-${qrData.type}-qr.png`;
    link.href = qrData.qrCode;
    link.click();
  };

  const copyUrl = () => {
    if (!qrData) return;
    navigator.clipboard.writeText(resolveUrl(qrData.url));
    toast.success("URL copied to clipboard");
  };

  return (
    <div className="space-y-6" data-testid="branches-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight">Branches</h1>
        <Button
          size="sm"
          className="rounded-sm"
          onClick={() => { setEditing(null); setForm({ name: "", address: "", phone: "" }); setShowForm(true); }}
          data-testid="add-branch-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> Add Branch
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <p className="text-muted-foreground">Loading...</p>
        ) : branchList.length === 0 ? (
          <p className="text-muted-foreground">No branches yet.</p>
        ) : (
          branchList.map((b) => (
            <Card
              key={b.id}
              className={`bg-card border-border rounded-sm transition-colors ${b.isDefault ? "border-primary/40 hover:border-primary/70" : "hover:border-primary/50"
                }`}
            >
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-primary shrink-0" />
                  <span className="flex-1 truncate">{b.name}</span>
                  {b.isDefault && (
                    <Badge
                      variant="outline"
                      className="text-[10px] px-1.5 py-0 border-primary/40 text-primary flex items-center gap-1 shrink-0"
                    >
                      <ShieldCheck className="h-2.5 w-2.5" />
                      Default
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {b.address && <p className="text-sm text-muted-foreground mb-1 whitespace-pre-line">{b.address}</p>}
                {b.phone && <p className="text-sm text-muted-foreground mb-3">{b.phone}</p>}

                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline" size="sm" className="rounded-sm"
                    onClick={() => handleQR(b, "signup")}
                    data-testid={`signup-qr-${b.id}`}
                  >
                    <UserPlus className="h-3 w-3 mr-1" /> Signup QR
                  </Button>
                  <Button
                    variant="outline" size="sm" className="rounded-sm"
                    onClick={() => handleQR(b, "attendance")}
                    data-testid={`attendance-qr-${b.id}`}
                  >
                    <CalendarCheck className="h-3 w-3 mr-1" /> Attendance QR
                  </Button>
                  <Button
                    variant="outline" size="sm" className="rounded-sm"
                    onClick={() => handleQR(b, "counter")}
                    data-testid={`counter-qr-${b.id}`}
                  >
                    <QrCode className="h-3 w-3 mr-1" /> Counter QR
                  </Button>
                  <Button
                    variant="outline" size="sm" className="rounded-sm"
                    onClick={() => { setEditing(b); setForm({ name: b.name, address: b.address || "", phone: b.phone || "" }); setShowForm(true); }}
                  >
                    <Pencil className="h-3 w-3 mr-1" /> Edit
                  </Button>

                  {!b.isDefault && (
                    <Button
                      variant="outline" size="sm" className="rounded-sm text-destructive hover:bg-destructive/10"
                      onClick={() => setDeleteModal({ open: true, branch: b })}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={showForm} onOpenChange={(o) => { if (!saving) setShowForm(o); }}>
        <DialogContent className="bg-card border-border rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit" : "Create"} Branch</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                className="rounded-sm mt-1"
                data-testid="branch-name"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Textarea
                value={form.address}
                onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
                className="rounded-sm mt-1 resize-none"
                rows={3}
                placeholder="Street, City, State..."
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                className="rounded-sm mt-1"
              />
            </div>
            <Button
              className="w-full rounded-sm"
              onClick={handleSave}
              disabled={saving}
              data-testid="save-branch-btn"
            >
              {saving ? "Saving..." : (editing ? "Update" : "Create") + " Branch"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR dialog */}
      <Dialog open={!!qrData} onOpenChange={() => { setQrData(null); setQrBranch(null); }}>
        <DialogContent className="bg-card border-border rounded-sm text-center max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {qrBranch?.name} — {qrData?.type === "signup" ? "Member Signup" : qrData?.type === "counter" ? "Counter Check-In" : "Attendance"} QR
            </DialogTitle>
          </DialogHeader>
          {qrData && (
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg inline-block mx-auto">
                <img src={qrData.qrCode} alt="QR Code" className="w-56 h-56" />
              </div>
              {/* Always show absolute URL */}
              <p className="text-xs text-muted-foreground break-all px-4">{resolveUrl(qrData.url)}</p>
              <p className="text-sm">
                {qrData.type === "signup"
                  ? "New members can scan this QR to fill out the registration form"
                  : qrData.type === "counter"
                  ? "Display this QR code at the gym counter. Members can scan it with their phone camera to check in/out."
                  : "Members scan this QR to check in/out"}
              </p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" size="sm" className="rounded-sm" onClick={copyUrl}>
                  <Copy className="h-4 w-4 mr-1" /> Copy URL
                </Button>
                <Button size="sm" className="rounded-sm" onClick={downloadQR}>
                  <Download className="h-4 w-4 mr-1" /> Download QR
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Modal */}
      <DeleteConfirmModal
        open={deleteModal.open}
        onOpenChange={(o) => setDeleteModal({ open: o, branch: o ? deleteModal.branch : null })}
        title="Delete Branch"
        description="This branch will be soft-deleted and removed from the list. Members and data associated with this branch are not affected and will remain intact."
        itemName={deleteModal.branch?.name}
        onConfirm={handleDelete}
        loading={deleteLoading}
      />
    </div>
  );
}
